/**
 * WorkspaceLayout — 3-column shell for Deep Explorer ("Workspace mode").
 *
 *   ┌──────────┬──────────────────────┬──────────────┐
 *   │ chat     │ workspace            │ activity     │
 *   │ rail     │  - chip strip        │ peek         │
 *   │ (slot)   │  - active artifact   │ (slot, with  │
 *   │          │  - history strip     │  collapse    │
 *   │ composer │                      │  toggle here)│
 *   └──────────┴──────────────────────┴──────────────┘
 *
 * The middle column is owned by this component: it pulls
 * `getLatestPerLineage(sessionId)` from the AI Explorer store and tracks
 * which artifact is "active" in local state. Switching versions or chips
 * is local state — no server round-trip, no store mutation.
 *
 * The chat rail (conversation + composer) and the activity peek are
 * supplied by the parent through slot props. The parent decides what to
 * render in those columns and how the activity peek expands/collapses;
 * this component only provides the column geometry. That keeps the layout
 * shell decoupled from the existing Chat compound component (which is
 * deeply integrated into `ai-explorer-page.tsx`) and from the existing
 * `DeepExplorerSidePanel` (which has its own collapse/hero machinery).
 *
 * On viewports narrower than the `md` Tailwind breakpoint (768px) the
 * 3-column layout collapses to a single column with a top tab bar
 * ("Chat" / "Workspace" / "Activity"). The chat composer stays anchored
 * at the bottom regardless of which tab is active so the user can fire
 * follow-up questions while looking at an artifact.
 *
 * Spec: `.workspace-spec/WORKSPACE_CONTRACT.md`
 */
import { useEffect, useMemo, useRef, useState } from "react"
import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { Artifact } from "@/api/models"

import { Sparkles } from "@/components/icons"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import { useViewportBelow } from "@/hooks/use-viewport-below"
import { cn } from "@/lib/utils"
import {
  getActiveArtifactId,
  getDeepStateForSession,
  getLatestPerLineage,
  getVersionChain,
  useAIExplorerStore,
} from "@/stores/ai-explorer-store"

import { type ArtifactLineage, getLineageGroupKey } from "./artifact-selector-utils"
import { ArtifactRenderer } from "./ArtifactRenderer"
import { ArtifactSelector } from "./ArtifactSelector"
import { computeWorkspacePhasePill } from "./workspace-phase-pill"

/**
 * Feature flag: kill-switch for the new Workspace layout in Deep mode.
 *
 * When `false`, integrators (`ai-explorer-page.tsx`) should fall back to
 * rendering the legacy Deep Explorer chrome (Chat + ContentSidebar +
 * DeepExplorerSidePanel). Flip to `false` if a regression slips through —
 * the legacy path doesn't depend on this module at all.
 */
export const WORKSPACE_LAYOUT_ENABLED = true

/** Module-level stable empty-array constant used by the version-chain
 *  selector so the no-active-artifact branch returns an `Object.is`-equal
 *  reference across renders (otherwise zustand re-renders forever). */
const EMPTY_VERSION_CHAIN: Artifact[] = []

/** Mobile tab identifiers. Kept tiny + stringly-typed so the tab bar can
 *  be a plain `button` row without pulling in shadcn `Tabs` machinery. */
export type MobileTab = "chat" | "workspace" | "activity"

export interface WorkspaceLayoutProps {
  /** Used to namespace the AI Explorer store (matches the rest of the app). */
  sessionType: string
  /** Active session id — keys into `artifactsByConversation`. */
  sessionId: string | null | undefined
  /** Chat rail content — the existing message list lives here. */
  conversationSlot: ReactNode
  /** Chat composer / footer content. */
  composerSlot: ReactNode
  /** Activity peek — typically the existing `DeepExplorerSidePanel` (or a
   *  parent-supplied wrapper that handles collapsed/expanded state). */
  activitySlot: ReactNode
  /** Bubble-up dispatcher. The dispatcher inside `ArtifactRenderer` already
   *  intercepts `copy` and `download`; everything else lands here. The
   *  optional 3rd arg is a contract extension used by:
   *    - `plan.reject`        → `{ feedback }`   (Phase 1 workspace-focus)
   *    - `ask_user.respond`   → `{ answers }`    (Phase 2 workspace-focus)
   */
  onAction?: (
    actionId: string,
    artifact: Artifact,
    extra?: {
      feedback?: string
      answers?: Record<string, string | string[]>
    }
  ) => void
  /** Optional: passes through to the active `ArtifactRenderer`. */
  onViewTrace?: (missionId: string) => void
  /** Search-service/library section used by inline citation controls. */
  citationSection?: string
  /**
   * Phase 6.x polish: page-level trace-focus state, mirror of the
   * `missionFilter` prop the parent passes into `DeepExplorerActivityPeek`.
   * When non-null in mobile mode the layout auto-switches to the Activity
   * tab so the user actually sees the filtered timeline (the peek itself
   * is already a sibling on the activity tab and will expand on its own).
   */
  traceFocusedMissionId?: string | null
  /**
   * Optional "next steps" footer rendered in the workspace center column
   * below the active artifact. Used by the parent to host
   * `<SmartFollowupCards>` after a deep run completes — keeping followup
   * suggestions adjacent to the deliverable they correspond to instead of
   * floating in the chat rail. Render `null` to hide.
   */
  followupsSlot?: ReactNode
  /** True iff the backend reports the agent run for this session is still
   *  active (status in {queued, running}). Used to gate the workspace
   *  phase pill — the pill should hide once the run is done, even if
   *  the activity log's tail is a still-pill-worthy status event (the
   *  agent often doesn't emit an explicit "phase complete" marker; the
   *  run just ends). Without this gate the pill would say e.g.
   *  "Reviewing answer quality…" forever after a run that ended on
   *  the QA phase (Erik feedback 2026-05-22). */
  agentRunActive?: boolean
  /** Optional className for the outermost flex row. */
  className?: string
}

function EmptyWorkspace() {
  const { t } = useTranslation()
  return (
    <div
      className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 p-8 text-center"
      data-testid="workspace-empty"
    >
      <Sparkles aria-hidden className="size-8 opacity-60" />
      <h3 className="text-foreground text-base font-medium">
        {t("deepExplorer.workspace_empty_title")}
      </h3>
      <p className="max-w-sm text-sm">{t("deepExplorer.workspace_empty_description")}</p>
    </div>
  )
}

interface MobileTabBarProps {
  active: MobileTab
  onChange: (tab: MobileTab) => void
}

/** Small, unobtrusive tab row used only on `<md` viewports. Plain buttons
 *  rather than shadcn `Tabs` because we need the tab state to live in the
 *  parent (so it can drive which slot is rendered) and a Radix root would
 *  add another layer for no benefit. */
function MobileTabBar({ active, onChange }: MobileTabBarProps) {
  const { t } = useTranslation()
  const tabs: { id: MobileTab; label: string }[] = [
    { id: "chat", label: t("deepExplorer.mobile_tab_chat") },
    { id: "workspace", label: t("deepExplorer.mobile_tab_workspace") },
    { id: "activity", label: t("deepExplorer.mobile_tab_activity") },
  ]
  return (
    <div
      className="bg-background flex shrink-0 items-stretch border-b"
      data-testid="workspace-mobile-tabs"
      role="tablist"
    >
      {tabs.map((t) => {
        const isActive = active === t.id
        return (
          <button
            key={t.id}
            aria-selected={isActive}
            data-active={isActive ? "true" : "false"}
            data-testid={`workspace-mobile-tab-${t.id}`}
            role="tab"
            type="button"
            className={cn(
              "flex-1 px-3 py-2 text-xs font-medium transition-colors",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
              isActive
                ? "border-primary text-foreground border-b-2"
                : "text-muted-foreground hover:text-foreground border-b-2 border-transparent"
            )}
            onClick={() => onChange(t.id)}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

export function WorkspaceLayout({
  sessionType,
  sessionId,
  conversationSlot,
  composerSlot,
  activitySlot,
  onAction,
  onViewTrace,
  citationSection,
  traceFocusedMissionId,
  followupsSlot,
  agentRunActive,
  className,
}: WorkspaceLayoutProps) {
  const { t } = useTranslation()
  const store = useAIExplorerStore(sessionType)
  // The selector return is referentially stable as long as the underlying
  // session list is unchanged (see `latestPerLineageCache` in the slice).
  const latestPerLineage = store((s) => getLatestPerLineage(s, sessionId))
  // Workspace phase pill — driven by the most recent pill-worthy backend
  // status event in the deep activity log. Replaces the older
  // ``isReplanning`` boolean from ``ai-explorer-page.tsx`` whose trigger
  // condition over-fired post-plan-approval: the page-level flag set
  // ``replanning=true`` for the ENTIRE post-approval execution phase,
  // so the pill said "Replanning…" while the agent was actually doing
  // qa_reviewer / synthesis / routine tool calls. Erik feedback 2026-05-21.
  //
  // The new pill is status-driven: reads the latest matching status
  // event (deep_workflow_restart, deep_plan_revising,
  // deep_quality_review_retry, deep_synthesis_retry,
  // deep_todo_execution_retry) and labels itself accordingly. During
  // quiet execution (no pill-worthy events recent), the pill hides —
  // the activity panel + chip strip already carry the signal in that
  // phase. See ``workspace-phase-pill.ts`` for the full mapping.
  const phaseActivityLog = store((s) => getDeepStateForSession(s, sessionId).activityLog)
  const phasePill = useMemo(
    () => computeWorkspacePhasePill(phaseActivityLog, t),
    [phaseActivityLog, t]
  )
  // When a plan is awaiting approval the workspace center MUST show the plan,
  // regardless of what activeArtifactIdByConversation currently points at.
  // Without this hard override, an ask_user artifact in the same session can
  // win the render even after the plan has been proposed (Daniel feedback
  // 2026-05-12 — "plan rendered for a second then back to Quick question").
  const planAwaitingApproval = store((s) => {
    const deep = getDeepStateForSession(s, sessionId)
    return deep.planProposed && !deep.planApproved
  })

  // Phase 4 (workspace-focus): the "active artifact id" is now driven by the
  // shared store so non-layout code (e.g. WorkerLanes lane-row clicks) can
  // focus an artifact by dispatching `focusArtifact(sessionId, id)`. Local
  // state still tracks per-component chip selection so the desktop chip-strip
  // path works without forcing every click to round-trip through zustand;
  // we converge the two by mirroring local clicks into the store as well.
  const storeActiveId = store((s) => getActiveArtifactId(s, sessionId))
  const focusArtifactAction = store((s) => s.focusArtifact)

  // Active artifact id. `null` means "show the freshest" — the effective id
  // is computed in the memo below so we react to incoming artifact_emit
  // frames automatically.
  const [activeId, setActiveId] = useState<string | null>(null)

  // Effective active id: prefer the store-driven focus when it resolves to a
  // known artifact in this session's lineage list. Local state is the
  // fallback (chip clicks, version chips). Store wins because lane → workspace
  // correlation is explicit user intent that overrides the chip-strip default.
  const storeActiveResolves = useMemo<boolean>(() => {
    if (!storeActiveId) return false
    return latestPerLineage.some((a) => a.id === storeActiveId)
  }, [storeActiveId, latestPerLineage])
  const resolvedActiveId = storeActiveResolves ? storeActiveId : activeId

  const activeArtifact = useMemo<Artifact | null>(() => {
    if (latestPerLineage.length === 0) return null
    // Hard override: when a plan is awaiting approval, the workspace MUST
    // render the latest plan artifact. Any other "active" (e.g. an unresolved
    // ask_user artifact that something is keeping pinned) is overridden until
    // the user approves or rejects the plan.
    if (planAwaitingApproval) {
      const latestPlan = [...latestPerLineage].reverse().find((a) => a.type === "plan")
      if (latestPlan) return latestPlan
    }
    if (resolvedActiveId) {
      const found = latestPerLineage.find((a) => a.id === resolvedActiveId)
      if (found) return found
      // Active id may belong to a non-latest version of a known lineage.
      // We still want to honour it (the user clicked a v1 chip), so fall
      // through to a separate path below using the version chain.
    }
    // Default: most recent emit. `getLatestPerLineage` preserves emit order;
    // newest is the last element.
    return latestPerLineage[latestPerLineage.length - 1] ?? null
  }, [resolvedActiveId, latestPerLineage, planAwaitingApproval])

  // Resolve the *actually-active* artifact when the resolved id points at a
  // non-latest version (the user clicked into history). We do this in a
  // separate selector so the lookup only fires when needed.
  const activeArtifactResolved = store((s) => {
    if (!resolvedActiveId) return null
    if (activeArtifact && activeArtifact.id === resolvedActiveId) return null
    if (!sessionId) return null
    // Record's indexed access lies about presence — at runtime a missing key
    // is undefined, so we have to look the slot up via hasOwnProperty.
    const map = s.artifactsByConversation
    if (!Object.prototype.hasOwnProperty.call(map, sessionId)) return null
    const list = map[sessionId]
    return list.find((a) => a.id === resolvedActiveId) ?? null
  })

  const effectiveActive = activeArtifactResolved ?? activeArtifact

  // Stable empty-chain reference — `getVersionChain` already returns a stable
  // `EMPTY_ARTIFACTS` constant when the lookup misses, but we need a matching
  // stable ref for the no-active-artifact branch so zustand's `Object.is`
  // equality stops re-rendering us into an infinite loop.
  const effectiveActiveId = effectiveActive ? effectiveActive.id : null
  const versionChain = store((s) =>
    effectiveActiveId ? getVersionChain(s, sessionId, effectiveActiveId) : EMPTY_VERSION_CHAIN
  )

  // If the active artifact disappears (e.g. session switched), drop selection.
  useEffect(() => {
    if (activeId && latestPerLineage.length === 0 && !activeArtifactResolved) {
      setActiveId(null)
    }
  }, [activeId, activeArtifactResolved, latestPerLineage])

  // Centralised chip/version click handler — keeps the local state and the
  // shared `activeArtifactIdByConversation` in sync so any future lane-side
  // dispatcher reads the same value the desktop chip strip is showing.
  const selectArtifact = (id: string) => {
    setActiveId(id)
    if (sessionId) focusArtifactAction(sessionId, id)
  }

  const hasArtifacts = latestPerLineage.length > 0

  // Newest-first order in the chip strip — the most recently emitted
  // lineage is what the user is actively iterating on.
  const chips = useMemo(() => [...latestPerLineage].reverse(), [latestPerLineage])
  const artifactLineages = useMemo<ArtifactLineage[]>(() => {
    const state = store.getState()
    const grouped = new Map<string, ArtifactLineage>()
    for (const latest of chips) {
      const versions = getVersionChain(state, sessionId, latest.id)
      const nextVersions = versions.length > 0 ? versions : [latest]
      // QA #R6: group by lineage root id, not `${type}:${title}`. Two
      // independent artifacts with the same type+title (e.g. two distinct
      // "report" documents the user generated in the same session) must
      // appear in separate chip groups. The lineage root is the first
      // entry of the parent-walked chain (or the artifact itself when
      // there's no parent).
      const root = nextVersions[0] ?? latest
      const key = getLineageGroupKey(latest, root.id)
      const existing = grouped.get(key)
      if (!existing) {
        grouped.set(key, { latest, versions: [...nextVersions] })
        continue
      }
      const seen = new Set(existing.versions.map((version) => version.id))
      for (const version of nextVersions) {
        if (!seen.has(version.id)) {
          existing.versions.push(version)
          seen.add(version.id)
        }
      }
      existing.versions.sort((left, right) => left.version - right.version)
    }
    return Array.from(grouped.values())
  }, [chips, sessionId, store])

  // QA polish #4 (DESIGN LOCKED): pulse dot on the newest chip when the
  // user has pinned focus to an older artifact. `latestId` is the most
  // recent emit (last element of the in-order list); we compare against the
  // store-driven focus so the indicator reflects explicit user pinning, not
  // local chip-strip selections that already converge into the store.
  const latestId = latestPerLineage[latestPerLineage.length - 1]?.id ?? null
  // Gate on `storeActiveResolves` — if the focused id no longer exists in the
  // current session's lineage (e.g. the user pinned to v1 and v2 then refined
  // it, dropping v1 from `latestPerLineage`), the workspace already falls
  // back to rendering the latest. In that case the latest IS being shown, so
  // pulsing its chip would be misleading ("newer thing exists" while you're
  // looking at it). Only pulse when the pin is real and points elsewhere.
  const isPinnedAway = Boolean(
    storeActiveResolves && storeActiveId && latestId && storeActiveId !== latestId
  )

  // ── Mobile (<md) responsive scaffolding ────────────────────────────
  // CSS-only would force us to render `composerSlot` in two DOM places
  // (inside the chat rail on desktop, pinned-bottom on mobile), which
  // would fork its internal state — focus, IME composition, refs to the
  // textarea, and any uncontrolled inputs. We use a viewport hook so each
  // slot mounts exactly once and its state survives breakpoint changes.
  const isMobile = useViewportBelow({ breakpoint: 768 })

  // Initial tab default: Workspace if there's already an artifact, else
  // Chat. We then auto-switch to Workspace on the *first* artifact arrival
  // when the user hasn't manually picked a tab, so the user isn't stuck on
  // an empty Chat tab while a fresh artifact appears in the background.
  const [activeTab, setActiveTab] = useState<MobileTab>(() => (hasArtifacts ? "workspace" : "chat"))
  const userPickedTabRef = useRef(false)
  const handleTabChange = (next: MobileTab) => {
    userPickedTabRef.current = true
    setActiveTab(next)
  }
  useEffect(() => {
    if (!userPickedTabRef.current && hasArtifacts && activeTab === "chat") {
      setActiveTab("workspace")
    }
  }, [hasArtifacts, activeTab])

  // QA polish #5: when the user clicks "View trace" in mobile mode, the
  // page-level state flips `traceFocusedMissionId` from null → set. The
  // activity peek lives on the Activity tab; without this auto-switch the
  // user's click would be silent (banner + filtered timeline render in a
  // tab they aren't looking at). Mirror the desktop behaviour where the
  // peek is always visible and force-expands itself on the same trigger.
  useEffect(() => {
    if (isMobile && traceFocusedMissionId) {
      setActiveTab("activity")
    }
  }, [isMobile, traceFocusedMissionId])

  const workspaceMain = hasArtifacts ? (
    <>
      {/* Chip strip */}
      <div
        className="bg-background/60 flex min-h-[45px] items-center gap-2 overflow-x-auto border-b px-3 py-2"
        data-testid="workspace-chip-strip"
      >
        {effectiveActive ? (
          <ArtifactSelector
            activeArtifact={effectiveActive}
            isLatestUnseen={isPinnedAway}
            lineages={artifactLineages}
            onSelect={selectArtifact}
          />
        ) : null}
        {/* Hide the phase pill when the agent run is no longer active —
         *  the activity log's tail can be a still-pill-worthy event
         *  (qa_reviewer typically finishes without emitting a "QA done"
         *  status), but if the run itself is reported DONE there's no
         *  current phase to indicate. Default to ``true`` for tests /
         *  callers that don't thread the prop through, preserving the
         *  log-driven behavior. */}
        {phasePill && agentRunActive !== false ? (
          <span
            aria-live="polite"
            data-phase-key={phasePill.translationKey}
            data-phase-level={phasePill.level}
            data-testid="workspace-phase-pill"
            className={cn(
              "inline-flex items-center gap-1.5",
              "rounded-full border px-2 py-0.5 text-[11px] font-medium tabular-nums",
              phasePill.level === "error"
                ? "border-destructive/40 bg-destructive/10 text-destructive"
                : "border-primary/40 bg-primary/10 text-primary",
              "motion-reduce:[&_svg]:animate-none"
            )}
          >
            <svg
              aria-hidden
              className="size-3 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="opacity-30"
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="3"
              />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="3"
              />
            </svg>
            {phasePill.label}
          </span>
        ) : null}
      </div>

      {/* Active artifact */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4" data-testid="workspace-active-artifact">
        {effectiveActive ? (
          <ArtifactRenderer
            artifact={effectiveActive}
            citationSection={citationSection}
            sessionType={sessionType}
            versionChain={versionChain}
            onAction={onAction}
            onSelectVersion={(id) => selectArtifact(id)}
            onViewTrace={onViewTrace}
          />
        ) : null}
        {/* "Next steps" suggestions — rendered after the active artifact so
         *  followup cards live next to the deliverable they correspond to,
         *  not in the chat rail. Parent passes a node (or null) and decides
         *  when it's ready to show. Wrapped here in a top-margin spacer so
         *  the parent slot doesn't have to know about workspace layout. */}
        {followupsSlot ? (
          <div className="mt-3" data-testid="workspace-followups-slot">
            {followupsSlot}
          </div>
        ) : null}
      </div>
    </>
  ) : (
    <EmptyWorkspace />
  )

  if (isMobile) {
    return (
      <div
        className={cn("flex h-full min-h-0 w-full flex-col overflow-hidden", className)}
        data-mobile="true"
        data-testid="workspace-layout"
      >
        <MobileTabBar active={activeTab} onChange={handleTabChange} />

        {/* Tab body — the composer is always rendered below this region,
         *  regardless of which tab is active, so the user can keep typing
         *  follow-ups while inspecting an artifact or activity peek. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeTab === "chat" ? (
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              data-testid="workspace-conversation-slot"
            >
              {conversationSlot}
            </div>
          ) : null}
          {activeTab === "workspace" ? (
            <main
              className="bg-muted/10 flex min-h-0 flex-1 flex-col overflow-hidden"
              data-testid="workspace-main"
            >
              {workspaceMain}
            </main>
          ) : null}
          {activeTab === "activity" ? (
            <div
              className="bg-background flex min-h-0 flex-1 flex-col overflow-y-auto"
              data-testid="workspace-activity-slot"
            >
              {activitySlot}
            </div>
          ) : null}
        </div>

        {/* Composer pinned at the bottom of the flex column — visible
         *  on every tab. No `position: fixed` needed: the parent's
         *  flex-column layout anchors this last child naturally. */}
        <div className="bg-background shrink-0 border-t" data-testid="workspace-composer-slot">
          {composerSlot}
        </div>
      </div>
    )
  }

  // Desktop layout: chat rail ↔ workspace are user-resizable via a drag
  // handle (persisted per browser via `autoSaveId`). The activity peek stays
  // a self-sized sibling because it manages its own collapsed/expanded width
  // (icon-only ↔ 440 px) — folding it into the resize group would force a
  // wide empty column when collapsed.
  return (
    <div
      className={cn("flex h-full min-h-0 w-full overflow-hidden", className)}
      data-testid="workspace-layout"
    >
      <ResizablePanelGroup
        autoSave="hello-ai:workspace-cols"
        className="min-w-0 flex-1"
        direction="horizontal"
      >
        {/* ── Chat rail ───────────────────────────────────────────────── */}
        {/* QA polish #6: chat-rail minSize bumped 15% → 18% so the composer
         *  pill buttons stop wrapping on narrow viewports (≈230 px floor at
         *  1280-wide vs 192 px before). Percentage-only first; if visible
         *  clipping persists in a smoke run, add an inline `minWidth: 220`. */}
        <ResizablePanel defaultSize="24%" maxSize="45%" minSize="18%">
          <aside
            className="bg-background flex h-full min-h-0 w-full flex-col border-r"
            data-testid="workspace-chat-rail"
          >
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              data-testid="workspace-conversation-slot"
            >
              {conversationSlot}
            </div>
            <div className="border-t" data-testid="workspace-composer-slot">
              {composerSlot}
            </div>
          </aside>
        </ResizablePanel>

        <ResizableHandle />

        {/* ── Workspace ───────────────────────────────────────────────── */}
        <ResizablePanel defaultSize="76%" minSize="40%">
          <main
            className="bg-muted/10 flex h-full min-h-0 flex-col overflow-hidden"
            data-testid="workspace-main"
          >
            {workspaceMain}
          </main>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* ── Activity peek ───────────────────────────────────────────── */}
      <aside
        className="bg-background flex h-full min-h-0 flex-shrink-0 flex-col border-l"
        data-testid="workspace-activity-slot"
      >
        {activitySlot}
      </aside>
    </div>
  )
}
